import React, { useState, useEffect } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Button,
  Divider,
  Stack,
  Paper,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";

import AppModal from "../common/AppModal";
import FormAlert from "../common/FormAlert";
import AppCircularProgress from "../common/AppCircularProgress";

import { useAppDispatch, useAppSelector } from "../../redux/store";
import {
  getAssessmentScoreRoster,
  saveAssessmentScoreBulk,
  resetScoreServerStatus,
} from "../../redux/actions/assessmentScoreActions";
import { assessmentScoreActions } from "../../redux/slices/assessmentScore";

import { AssessmentResponseDto } from "../../shared/dtos/assessment/AssessmentResponseDto";
import { AssessmentScoreRosterResponseDto } from "../../shared/dtos/assessmentScore/AssessmentScoreRosterResponseDto";
import { AssessmentScoreBulkRequestDto } from "../../shared/dtos/assessmentScore/AssessmentScoreBulkRequestDto";

interface LocalRosterItem extends AssessmentScoreRosterResponseDto {
  comments?: string | null;
}

interface AssessmentScoreRecordModalProps {
  open: boolean;
  onClose: () => void;
  assessment: AssessmentResponseDto;
}

const AssessmentScoreRecordModal: React.FC<AssessmentScoreRecordModalProps> = ({
  open,
  onClose,
  assessment,
}) => {
  const dispatch = useAppDispatch();

  // --- Redux State ---
  const {
    scoreRoster,
    loadingScoreRoster,
    loadingScoreBulk,
    errorSavingScores,
    serverStatus,
  } = useAppSelector((state) => state.assessmentScore);
  const { userInfo } = useAppSelector((state) => state.auth);

  // --- Local State ---
  const [localRoster, setLocalRoster] = useState<LocalRosterItem[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);

  // 1. Fetch roster on open
  useEffect(() => {
    if (open && assessment?.id) {
      dispatch(getAssessmentScoreRoster(assessment.id));
    }
  }, [dispatch, open, assessment?.id]);

  // 2. Sync local state
  useEffect(() => {
    if (scoreRoster) {
      setLocalRoster(
        scoreRoster.map((item) => ({
          ...item,
          marksObtained: item.marksObtained ?? null,
          comments: item.comments || "",
        })),
      );
    }
  }, [scoreRoster]);

  // 3. Auto-close on success
  useEffect(() => {
    if (
      serverStatus?.operation === "bulk-save" &&
      serverStatus?.status === 201
    ) {
      dispatch(resetScoreServerStatus());
      onClose();
    }
  }, [serverStatus, dispatch, onClose]);

  const handleMarkChange = (enrollmentId: string, value: string): void => {
    const numValue = value === "" ? null : parseFloat(value);

    // Validation against maxMarks
    if (numValue !== null && numValue > (assessment.maxMarks || 100)) {
      setValidationError(`Marks cannot exceed ${assessment.maxMarks}`);
    } else if (numValue !== null && numValue < 0) {
      setValidationError("Marks cannot be negative");
    } else {
      setValidationError(null);
    }

    setLocalRoster((prev) =>
      prev.map((r) =>
        r.enrollmentId === enrollmentId ? { ...r, marksObtained: numValue } : r,
      ),
    );
  };

  const handleCommentChange = (enrollmentId: string, comment: string): void => {
    setLocalRoster((prev) =>
      prev.map((r) =>
        r.enrollmentId === enrollmentId ? { ...r, comments: comment } : r,
      ),
    );
  };

  const handleSave = (): void => {
    if (!assessment.id || !userInfo?.id) return;

    const payload: AssessmentScoreBulkRequestDto = {
      assessmentId: assessment.id,
      gradedByTeacherId: userInfo.id,
      scores: localRoster.map((r) => ({
        studentId: r.studentId,
        enrollmentId: r.enrollmentId,
        marksObtained:
          r.marksObtained !== null && r.marksObtained !== undefined
            ? Number(r.marksObtained)
            : null,
        comments: r.comments || null,
      })),
    };

    dispatch(saveAssessmentScoreBulk(payload));
  };

  const activeError = validationError || errorSavingScores;

  return (
    <AppModal
      open={open}
      onClose={onClose}
      title={`Marking Sheet: ${assessment.name}`}
      width="lg"
      loading={loadingScoreBulk}
    >
      <Stack spacing={2} sx={{ width: "100%" }}>
        {/* Assessment Specs Bar */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
            gap: 2,
            p: 1.5,
            bgcolor: "background.paper",
            borderRadius: 1,
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Subject: <b>{assessment.subjectName}</b>
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Max Marks: <b>{assessment.maxMarks}</b>
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Weightage: <b>{assessment.weightage ?? 100}%</b>
          </Typography>
        </Box>

        {/* Reusable Form Alert */}
        <FormAlert
          message={activeError}
          severity="error"
          closable
          onClose={() => {
            setValidationError(null);
            dispatch(assessmentScoreActions.setErrorSavingScores(null));
          }}
        />

        {loadingScoreRoster ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              py: 8,
            }}
          >
            <AppCircularProgress />
            <Typography sx={{ ml: 2, color: "text.secondary" }}>
              Loading Student Roster...
            </Typography>
          </Box>
        ) : (
          <TableContainer
            component={Paper}
            variant="outlined"
            sx={{ maxHeight: 450, borderRadius: 1 }}
          >
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "action.hover" }}>
                  <TableCell sx={{ fontWeight: 700 }}>Student Name</TableCell>
                  <TableCell sx={{ fontWeight: 700, width: "160px" }}>
                    Marks / {assessment.maxMarks}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>
                    Comments / Remarks
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {localRoster.map((row) => {
                  const isOverLimit =
                    row.marksObtained !== null &&
                    row.marksObtained !== undefined &&
                    row.marksObtained > (assessment.maxMarks || 100);

                  return (
                    <TableRow key={row.enrollmentId} hover>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {row.studentName}
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.secondary", display: "block" }}
                        >
                          {row.admissionNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <TextField
                          type="number"
                          fullWidth
                          size="small"
                          slotProps={{
                            htmlInput: {
                              max: assessment.maxMarks,
                              min: 0,
                              step: "0.5",
                            },
                          }}
                          value={row.marksObtained ?? ""}
                          onChange={(e) =>
                            handleMarkChange(row.enrollmentId, e.target.value)
                          }
                          error={Boolean(isOverLimit)}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Optional feedback..."
                          value={row.comments || ""}
                          onChange={(e) =>
                            handleCommentChange(
                              row.enrollmentId,
                              e.target.value,
                            )
                          }
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}

                {localRoster.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={3}
                      align="center"
                      sx={{ py: 4, color: "text.secondary" }}
                    >
                      No enrolled students found in this class.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <Divider sx={{ my: 1 }} />

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
          <Button onClick={onClose} color="inherit" disabled={loadingScoreBulk}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={
              loadingScoreBulk || !!validationError || localRoster.length === 0
            }
          >
            {loadingScoreBulk ? "Saving..." : "Save Marks"}
          </Button>
        </Box>
      </Stack>
    </AppModal>
  );
};

export default AssessmentScoreRecordModal;
